import { useCallback, useEffect, useRef, useState } from "react";
import { MapPin, Send, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAnnotation } from "./annotation-context";
import { useCreateComment } from "@/hooks/use-watch";
import { VisibilityDropdown, type CommentVisibility } from "./visibility-dropdown";

type Props = {
  mode: "workspace" | "guest" | "share";
  watchKey: string | undefined;
  currentVersionId: string | null;
  getCurrentTime: () => number;
  /** Asset type of the version being viewed (2026-07-18). Backend enforces
   *  that image comments must NOT carry timestampSeconds — so when the anchor
   *  drop happens on an image, the POST body sends `timestampSeconds: null`
   *  regardless of what `getCurrentTime()` returns. Default = "video" for
   *  backward-compat with existing video callers. */
  assetType?: "video" | "audio" | "image";
};

/**
 * Anchor pin drop overlay + floating composer (Task 13 rework).
 *
 * Wholly separate from the annotation flow — no shapes, no toolbar. When
 * `anchorMode` is on:
 *   - This layer captures pointer events over the video canvas
 *   - Cursor becomes a pin
 *   - Click drops a pin at that 0-1 location (`pendingPin`)
 *   - A small floating composer appears anchored to the pin
 *   - Send posts the comment with `positionX`/`positionY` (spatial anchor)
 *     PLUS `timestampSeconds` (current playhead). Backend accepts both since
 *     the 2026-07-11 endpoint relaxation.
 *   - Success exits anchor mode; failure leaves the composer open for retry
 *   - Cancel / Esc clears the pending pin without posting; can drop again
 */
export function AnchorPinOverlay({ mode, watchKey, currentVersionId, getCurrentTime, assetType = "video" }: Props) {
  const { anchorMode, pendingPin, setPendingPin, deactivateAnchor } = useAnnotation();
  const overlayRef = useRef<HTMLDivElement>(null);

  const onOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = overlayRef.current?.getBoundingClientRect();
      if (!rect) return;
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      // Clamp defensively; a click on the very edge could round outside [0,1].
      setPendingPin({
        x: Math.min(1, Math.max(0, x)),
        y: Math.min(1, Math.max(0, y)),
      });
    },
    [setPendingPin],
  );

  // Esc: cancel the pending pin OR exit anchor mode outright.
  useEffect(() => {
    if (!anchorMode) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        if (pendingPin) setPendingPin(null);
        else deactivateAnchor();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [anchorMode, pendingPin, setPendingPin, deactivateAnchor]);

  if (!anchorMode) return null;

  return (
    <div
      ref={overlayRef}
      onClick={pendingPin ? undefined : onOverlayClick}
      className="absolute inset-0 z-20"
      style={{
        // Custom SVG-data cursor so it looks like a map-pin instead of the
        // browser's default crosshair. The hotspot is bottom-center (the tip).
        cursor: pendingPin
          ? "default"
          // Smaller (18x24), white body, black border. Hotspot at tip (9, 24).
          : "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='18' height='24' viewBox='0 0 18 24'><path d='M9 0C4.05 0 0 4.05 0 9c0 6.3 9 15 9 15s9-8.7 9-15C18 4.05 13.95 0 9 0z' fill='white' stroke='black' stroke-width='1.5'/><circle cx='9' cy='9' r='2.5' fill='black'/></svg>\") 9 24, crosshair",
      }}
    >
      {pendingPin && (
        <>
          {/* Dropped pin marker on the video canvas at the click location.
              White body / black border — matches the drop cursor + the
              persistent anchor-pins display in AnchorPinsDisplay. */}
          <div
            className="pointer-events-none absolute"
            style={{
              left: `${pendingPin.x * 100}%`,
              top: `${pendingPin.y * 100}%`,
              transform: "translate(-50%, -100%)",
            }}
          >
            <MapPin
              className="h-6 w-6 fill-white text-black drop-shadow-lg"
              strokeWidth={2}
            />
          </div>

          {/* Floating composer anchored to the pin. Positioning tries not to
              fall off the visible area — mirrors x based on which half of the
              canvas the pin is in. */}
          <FloatingComposer
            pin={pendingPin}
            mode={mode}
            watchKey={watchKey}
            currentVersionId={currentVersionId}
            getCurrentTime={getCurrentTime}
            assetType={assetType}
            onDone={() => {
              setPendingPin(null);
              deactivateAnchor();
            }}
            onCancel={() => setPendingPin(null)}
          />
        </>
      )}
    </div>
  );
}

function FloatingComposer({
  pin,
  mode,
  watchKey,
  currentVersionId,
  getCurrentTime,
  assetType,
  onDone,
  onCancel,
}: {
  pin: { x: number; y: number };
  mode: "workspace" | "guest" | "share";
  watchKey: string | undefined;
  currentVersionId: string | null;
  getCurrentTime: () => number;
  assetType: "video" | "audio" | "image";
  onDone: () => void;
  onCancel: () => void;
}) {
  const [content, setContent] = useState("");
  const [visibility, setVisibility] = useState<CommentVisibility>("public");
  const create = useCreateComment(mode, watchKey);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  function submit() {
    const trimmed = content.trim();
    if (!trimmed || !currentVersionId) return;
    // Backend rule: image comments carry positionX/Y only, NEVER
    // timestampSeconds. Video + audio carry both.
    create.mutate(
      {
        currentVersionId,
        content: trimmed,
        timestampSeconds: assetType === "image" ? null : getCurrentTime(),
        positionX: pin.x,
        positionY: pin.y,
        visibility,
      },
      { onSuccess: onDone },
    );
  }

  // If pin is on the right half of the canvas, open composer to the LEFT of the
  // pin; otherwise open to the RIGHT. Keeps it on-canvas most of the time.
  const openLeft = pin.x > 0.5;

  return (
    <div
      className="absolute z-30 w-72 rounded-lg border border-border bg-popover p-2 shadow-xl"
      style={{
        left: `${pin.x * 100}%`,
        top: `${pin.y * 100}%`,
        transform: openLeft ? "translate(calc(-100% - 12px), -50%)" : "translate(12px, -50%)",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="mb-1 flex items-center justify-between px-1">
        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-primary">
          <MapPin className="h-3 w-3" /> Anchor comment
        </span>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancel"
          className="grid h-5 w-5 place-items-center rounded text-muted-foreground hover:bg-secondary hover:text-foreground"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
      <textarea
        ref={textareaRef}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            submit();
          }
          if (e.key === "Escape") onCancel();
        }}
        rows={3}
        placeholder="Leave a comment at this spot…"
        className="w-full resize-none rounded-md border border-border bg-background p-2 text-sm text-foreground outline-none focus:border-ring"
      />
      <div className="mt-1.5 flex items-center gap-2">
        <VisibilityDropdown value={visibility} onChange={setVisibility} compact />
        <button
          type="button"
          onClick={onCancel}
          className="ml-auto text-xs text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!content.trim() || create.isPending || !currentVersionId}
          className={cn(
            "inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs font-medium transition-colors",
            content.trim() && !create.isPending
              ? "bg-primary text-primary-foreground hover:bg-primary/90"
              : "bg-secondary text-muted-foreground",
          )}
        >
          <Send className="h-3 w-3" /> {create.isPending ? "Sending…" : "Send"}
        </button>
      </div>
      {create.error && (
        <div className="mt-1 truncate px-1 text-[11px] text-destructive">
          {(create.error as Error).message || "Failed to send"}
        </div>
      )}
    </div>
  );
}
